import { z } from "zod";
import { billingCreditIssuePayloadSchema } from "./billingCreditIssue.js";
import { changePlanOrPricePayloadSchema } from "./changePlanOrPrice.js";
import { cancelOrDeleteAccountPayloadSchema } from "./cancelOrDeleteAccount.js";
import { contractCommitmentSendPayloadSchema } from "./contractCommitmentSend.js";
import { issueCreditPayloadSchema } from "./issueCredit.js";
import { issueRefundPayloadSchema } from "./issueRefund.js";
import { sendExternalMessagePayloadSchema } from "./sendExternalMessage.js";
import { envelopeV2FieldsSchema } from "./envelope.js";
import type { ActionType } from "../types/actionTypes.js";

export const actionPayloadByType: Record<ActionType, z.ZodType> = {
  issue_refund: issueRefundPayloadSchema,
  issue_credit: issueCreditPayloadSchema,
  "billing.credit.issue": billingCreditIssuePayloadSchema,
  "contract.commitment.send": contractCommitmentSendPayloadSchema,
  change_plan_or_price: changePlanOrPricePayloadSchema,
  cancel_or_delete_account: cancelOrDeleteAccountPayloadSchema,
  send_external_message: sendExternalMessagePayloadSchema
};

const baseFields = {
  resource_ref: z.string().min(1),
  risk_level: z.enum(["low", "medium", "high"]).optional(),
  customer_tier: z.enum(["free", "pro", "enterprise", "smb"]).optional(),
  action_subtype: z.string().min(1).optional(),
  labels: z.record(z.string(), z.string()).optional()
};

export const authorizeRequestBodySchema = z.discriminatedUnion("action_type", [
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("issue_refund"),
      payload: issueRefundPayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema),
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("issue_credit"),
      payload: issueCreditPayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema),
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("billing.credit.issue"),
      payload: billingCreditIssuePayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema),
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("contract.commitment.send"),
      payload: contractCommitmentSendPayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema),
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("change_plan_or_price"),
      payload: changePlanOrPricePayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema),
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("cancel_or_delete_account"),
      payload: cancelOrDeleteAccountPayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema),
  z
    .object({
      environment: z.enum(["sandbox", "production"]),
      action_type: z.literal("send_external_message"),
      payload: sendExternalMessagePayloadSchema,
      ...baseFields
    })
    .merge(envelopeV2FieldsSchema)
]);

export type AuthorizeRequestBody = z.infer<typeof authorizeRequestBodySchema>;

export { changePlanOrPricePayloadSchema } from "./changePlanOrPrice.js";
export { cancelOrDeleteAccountPayloadSchema } from "./cancelOrDeleteAccount.js";
export { billingCreditIssuePayloadSchema } from "./billingCreditIssue.js";
export { contractCommitmentSendPayloadSchema } from "./contractCommitmentSend.js";
export { issueCreditPayloadSchema } from "./issueCredit.js";
export { issueRefundPayloadSchema } from "./issueRefund.js";
export { sendExternalMessagePayloadSchema } from "./sendExternalMessage.js";
export {
  envelopeV2FieldsSchema,
  agentMetaSchema,
  userDelegationSchema,
  requestContextSchema,
  toolMetaSchema,
  computeEnvelopeFingerprint,
  deriveRiskClass,
  originSystems,
  agentVendors,
  riskClasses,
  expectedSideEffects
} from "./envelope.js";
export type {
  EnvelopeV2Fields,
  AgentMeta,
  UserDelegation,
  RequestContext,
  ToolMeta,
  OriginSystem,
  AgentVendor,
  RiskClass,
  ExpectedSideEffect
} from "./envelope.js";
